import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocaleProvider } from "@/context/LocaleContext";
import AppLayout from "@/components/AppLayout";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Research from "@/pages/Research";
import Library from "@/pages/Library";
import Synergy from "@/pages/Synergy";
import Patterns from "@/pages/Patterns";
import Board from "@/pages/Board";
import FirmDetail from "@/pages/FirmDetail";
import Partners from "@/pages/Partners";
import StretchPartners from "@/pages/StretchPartners";
import Network from "@/pages/Network";
import Pitch from "@/pages/Pitch";
import PublicPitch from "@/pages/PublicPitch";
import PublicFirm from "@/pages/PublicFirm";
import Stakeholders from "@/pages/Stakeholders";
import Landing from "@/pages/Landing";
import Requests from "@/pages/Requests";
import Deliveries from "@/pages/Deliveries";
import SettingsPage from "@/pages/Settings";
import MutualFitMatrix from "@/pages/MutualFitMatrix";
import Methodology from "@/pages/Methodology";
import Upgrade from "@/pages/Upgrade";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Revenue from "@/pages/Revenue";

// Role-aware route guards.
// AdminOnly: clients hitting an admin-only route get redirected to /app/requests.
function AdminOnly({ children }) {
    const { user } = useAuth();
    if (user === null) return null; // still bootstrapping
    if (!user || user.role !== "admin") return <Navigate to="/app/requests" replace />;
    return children;
}
// DashboardOrRequests: "/app" index renders Dashboard for admins, Requests for clients.
function DashboardOrRequests() {
    const { user } = useAuth();
    if (user === null) return null;
    if (user && user.role === "admin") return <Dashboard />;
    return <Requests />;
}

function App() {
    return (
        <LocaleProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<AuthPage mode="login" />} />
                        <Route path="/signup" element={<AuthPage mode="register" />} />
                        <Route path="/p/:token" element={<PublicPitch />} />
                        <Route path="/p/firm/:token" element={<PublicFirm />} />
                        <Route path="/methodology" element={<Methodology />} />
                        <Route path="/app" element={<AppLayout />}>
                            <Route index element={<DashboardOrRequests />} />
                            <Route path="requests" element={<Requests />} />
                            <Route path="deliveries" element={<Deliveries />} />
                            <Route path="research" element={<AdminOnly><Research /></AdminOnly>} />
                            <Route path="library" element={<AdminOnly><Library /></AdminOnly>} />
                            <Route path="synergy" element={<AdminOnly><Synergy /></AdminOnly>} />
                            <Route path="patterns" element={<AdminOnly><Patterns /></AdminOnly>} />
                            <Route path="board" element={<AdminOnly><Board /></AdminOnly>} />
                            <Route path="firms/:id" element={<AdminOnly><FirmDetail /></AdminOnly>} />
                            <Route path="firms/:id/partners" element={<AdminOnly><Partners /></AdminOnly>} />
                            <Route path="firms/:id/stretch" element={<AdminOnly><StretchPartners /></AdminOnly>} />
                            <Route path="firms/:id/stakeholders" element={<AdminOnly><Stakeholders /></AdminOnly>} />
                            <Route path="network" element={<AdminOnly><Network /></AdminOnly>} />
                            <Route path="matrix" element={<AdminOnly><MutualFitMatrix /></AdminOnly>} />
                            <Route path="pitch" element={<AdminOnly><Pitch /></AdminOnly>} />
                            <Route path="upgrade" element={<Upgrade />} />
                            <Route path="payments/success" element={<PaymentSuccess />} />
                            <Route path="revenue" element={<AdminOnly><Revenue /></AdminOnly>} />
                            <Route path="settings" element={<SettingsPage />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/app" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </LocaleProvider>
    );
}

export default App;
